import {
  Component,
  Input,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { resolveWidgetTextColor } from '../../helpers/widget-color.helper';

/**
 * BooleanYesNo Widget Component
 * Displays "Yes" or "No" text based on a boolean value with full i18n support.
 * Supports color customization and optional action callbacks.
 */
@Component({
  selector: 'app-boolean-yes-no-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatTooltipModule],
  templateUrl: './boolean-yes-no-widget.component.html',
  styleUrls: ['./boolean-yes-no-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BooleanYesNoWidgetComponent {
  @Input() value: boolean = false;
  @Input() translationPrefix?: string;
  @Input() yesColor?: string;
  @Input() noColor?: string;
  @Input() tooltip?: string | ((value: boolean) => string); // Optional tooltip as string or function
  @Input() action?: (value: boolean) => void;
  @Input() translate: boolean = false; // Flag to enable i18n

  /**
   * Get the color class for the text
   */
  get textColor(): string {
    const colorName = this.value ? this.yesColor : this.noColor;

    return resolveWidgetTextColor({ color: colorName });
  }

  /**
   * Get tooltip key for template
   * If tooltip is a function, call it with the boolean value
   * If translate=true with translationPrefix, build full translation key for both string and callback results
   * Otherwise return as-is
   */
  get tooltipKey(): string | null {
    if (!this.tooltip) return null;

    // If tooltip is a function, call it
    const tooltipValue =
      typeof this.tooltip === 'function'
        ? this.tooltip(this.value)
        : this.tooltip;

    // If translate is enabled and we have a prefix, build full translation key
    if (this.translate && this.translationPrefix) {
      return `${this.translationPrefix}.${tooltipValue}`;
    }

    return tooltipValue;
  }

  /**
   * Handle click event
   */
  onClick(): void {
    if (this.action) {
      this.action(this.value);
    }
  }
}
